import { useMemo, useState } from 'react';
import type { AppointmentSeries, Staff } from '@salonomy/types';
import { Button, Card, Pill } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useCancelSeries, useSeriesList } from '../../data/recurring.js';
import { chairStaff, useSalon, useStaff } from '../../data/salon.js';
import { useServices } from '../../data/services.js';
import { useAuth } from '../../lib/auth.js';
import { clockLabelAt, longDate, isoDateInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { cadenceLabel } from './cadence.js';
import { EditSeriesModal } from './EditSeriesModal.js';
import { RecurringSeriesModal } from './RecurringSeriesModal.js';

/**
 * WS7 phase 3 — the Settings "Recurring appointments" surface. Replaces the ComingSoon scaffold in
 * SettingsPage. Lists the salon's active standing bookings, creates new ones, and per-series offers
 * edit-series (apply forward) and cancel-remaining (typed-confirmation destructive action).
 *
 * Self-fetching (salon / staff / services / series) so Integrate can mount it with no props. RLS
 * already scopes the series list to what the caller may see; we additionally gate the staff the
 * pickers offer to the caller's RBAC scope — a manager sees every chair, a non-manager sees only
 * themselves (the same manager-or-self rule the rota editor uses). "Edit this one" (detach a single
 * occurrence) is a per-appointment action handled from the diary, not from this list.
 */
export function RecurringSeriesPanel() {
  const { isManager, user } = useAuth();
  const { data: salon } = useSalon();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: series = [] } = useSeriesList({ status: 'active' });
  const cancel = useCancelSeries();
  const toast = useToast();

  const tz = salon?.timezone ?? 'Europe/London';
  const allChairs = useMemo(() => chairStaff(staff), [staff]);

  // RBAC: a manager books a series for any chair; a non-manager (stylist / chair_renter) only for
  // themselves. Reception (no staff record / chair) can't create one at all.
  const me = (staff ?? []).find((s) => s.id === user?.staffId);
  const allowedChairs: Staff[] = isManager
    ? allChairs
    : me && ['owner', 'stylist', 'chair_renter'].includes(me.role)
      ? [me]
      : [];
  const canCreate = allowedChairs.length > 0;

  const [createOpen, setCreateOpen] = useState(false);
  const [editSeries, setEditSeries] = useState<AppointmentSeries | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AppointmentSeries | null>(null);

  const staffName = (id: string) =>
    (staff ?? []).find((s) => s.id === id)?.displayName ?? 'a stylist';
  const serviceName = (id: string) => (services ?? []).find((s) => s.id === id)?.name ?? 'service';

  const today = isoDateInZone(new Date(), tz);

  async function confirmCancel() {
    if (!cancelTarget) return;
    try {
      await cancel.mutateAsync(cancelTarget.id);
      toast.success('Remaining bookings cancelled');
      setCancelTarget(null);
    } catch {
      toast.error('Could not cancel the remaining bookings.');
    }
  }

  const sorted = [...series].sort((a, b) => a.firstStartAt.localeCompare(b.firstStartAt));

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className="section-title">Recurring appointments</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Standing weekly or fortnightly bookings — set the cadence once. Creation shows which
            dates can’t fit; never dropped silently.
          </div>
        </div>
        {canCreate ? (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            New standing booking
          </Button>
        ) : (
          <span
            className="muted"
            style={{ fontSize: 11.5 }}
            title="Reception accounts don’t hold a chair, so they can’t own a standing booking."
          >
            View only
          </span>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          No standing bookings yet.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {sorted.map((s) => {
            const canEdit = isManager || s.staffId === user?.staffId;
            return (
              <div key={s.id} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {s.clientDisplayName || 'Standing booking'}
                    {s.renterId ? (
                      <Pill tone="soft" className="mono">
                        Sealed
                      </Pill>
                    ) : null}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {serviceName(s.serviceId)} · {cadenceLabel(s.intervalWeeks)} ·{' '}
                    {s.occurrenceCount} {s.occurrenceCount === 1 ? 'date' : 'dates'} · with{' '}
                    {staffName(s.staffId)}
                  </div>
                  <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                    from {longDate(isoDateInZone(new Date(s.firstStartAt), tz), tz)} at{' '}
                    {clockLabelAt(s.firstStartAt, tz)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditSeries(s)}>
                        Edit series
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(s)}>
                        Cancel remaining
                      </Button>
                    </>
                  ) : (
                    <span
                      className="muted"
                      style={{ fontSize: 11.5 }}
                      title="Only the owner can edit"
                    >
                      Sealed to {staffName(s.staffId)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {createOpen ? (
        <RecurringSeriesModal
          date={today}
          tz={tz}
          chairs={allowedChairs}
          services={services ?? []}
          onClose={() => setCreateOpen(false)}
        />
      ) : null}

      {editSeries ? (
        <EditSeriesModal
          series={editSeries}
          tz={tz}
          chairs={allowedChairs.length ? allowedChairs : allChairs}
          services={services ?? []}
          onClose={() => setEditSeries(null)}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(cancelTarget)}
        title="Cancel remaining bookings"
        confirmLabel="Cancel remaining"
        // Always require a typed confirmation for this destructive-with-future-bookings action
        // (brief non-negotiable). Use the client name where present, else a plain "cancel".
        confirmPhrase={cancelTarget?.clientDisplayName?.trim() || 'cancel'}
        busy={cancel.isPending}
        onConfirm={confirmCancel}
        onClose={() => setCancelTarget(null)}
      >
        <p style={{ margin: '0 0 8px' }}>
          This cancels every <strong>future, not-yet-started</strong> booking in{' '}
          <strong>{cancelTarget?.clientDisplayName || 'this series'}</strong>’s standing series and
          ends the series.
        </p>
        <p style={{ margin: 0 }}>
          Past and in-progress appointments are kept. This can’t be undone — you’d create a new
          standing booking to resume.
        </p>
      </ConfirmModal>
    </Card>
  );
}
